import {ServiceTemplateSettings} from "./templates";

/**
 * ServiceDirectorySettings описывает типизацию параметров настройки
 * директорий дополнительных услуг.
 */
export class ServiceDirectorySettings {
  /**
   * Порядковый номер для сортировки в селекторе директорий, а так же
   * в табуляторе виджета. Сортировка производится по возрастанию.
   * Влияет на отображение селектора родительской директории.
   */
  public PositionInSelect: number = 0;

  /**
   * Локализованное название селектора выбора дочерних директорий.
   * Может содержать как простой текст, так и код локализации.
   * Если оставить пустым - не выводится.
   */
  public SelectName: string = "";

  /**
   * Локализованный placeholder селектора выбора дочерних директорий.
   * Может содержать как простой текст, так и код локализации.
   * Если оставить пустым - не выводится.
   */
  public SelectPlaceholder: string = "";

  /**
   * Локализованная подсказка для селектора выбора дочерних директорий.
   * Может содержать как простой текст, так и код локализации.
   * Если оставить пустым - не выводится.
   */
  public SelectHelper: string = "";

  /**
   * SelectBlockSize содержит размер блока для отображения селектора.
   * Размер блока задается в колонках в диапазоне от 1 до 12.
   * Размер колонки вычисляется автоматически в зависимости от ширины контента.
   * Размер в 12 колонок означает 100% ширину относительно контента.
   */
  public SelectBlockSize: number = 2;

  /**
   * SelectWidth содержит ширину селектора в блоке.
   * Размер селектора задается в колонках в диапазоне от 1 до 12.
   * Размер колонки вычисляется автоматически в зависимости от ширины блока селектора.
   * Размер в 12 колонок означает 100% ширину относительно блока.
   */
  public SelectWidth: number = 12;

  /**
   * SelectSize содержит размер селектора в стилистике MUI.
   * Данное поле должно содержать одно из перечисленных значений:
   *   - small - Малый размер поля
   *   - normal - Нормальный размер поля
   */
  public SelectSize: string = "normal";

  /**
   * SelectNameDirection содержит настройку выравнивания для названия селектора.
   * Доступные варианты для выбора:
   *  - left - По левому краю
   *  - right - По правому краю
   *  - center - По центру
   */
  public SelectNameDirection: string = "left";

  /**
   * TemplateSettings содержит настройки шаблонов
   */
  public TemplateSettings: ServiceTemplateSettings = ServiceTemplateSettings.RestoreSettings({})

  /**
   * Заглушка для запрета прямого создания объектов данного типа
   * @private
   */
  private constructor() {
  }

  /**
   * RestoreSettings выполняет восстановление данных объекта настроек
   * Функция выполняет нормализацию значений настроек.
   * Функция дополняет недостающие настройки значениями по умолчанию.
   *
   * @param data
   */
  public static RestoreSettings(data: any): ServiceDirectorySettings {
    let result = new ServiceDirectorySettings();

    if (typeof data === "string") {
      data = JSON.parse(data)
    }

    if (typeof data !== "object") {
      return result;
    }

    Object.keys(result).map((key: keyof ServiceDirectorySettings) => {
      // @ts-ignore
      result[key] = ServiceDirectorySettings.GetCorrectFieldValue(key, data[key])
    })

    return result;
  }

  /**
   * GetCorrectFieldValue выполняет восстановление данных поля из переданного значения настроек.
   * Данный метод так же проводит валидацию значения каждой из настроек поля. Метод возвращает
   * значение по умолчанию, если значение передано не корректно.
   */
  public static GetCorrectFieldValue<T extends keyof ServiceDirectorySettings>(field: T, data: any): any {
    let defaults = new ServiceDirectorySettings()

    if (data === undefined) {
      return defaults[field]
    }

    switch (field) {
      case "SelectBlockSize":
      case "SelectWidth":
        return typeof data === "number" && data >= 1 && data <= 12 ? data : defaults[field]
      case "SelectSize":
        return ["small", "normal"].indexOf(data) !== -1 ? data : defaults[field]
      case "SelectNameDirection":
        return ["left", "right", "center"].indexOf(data) !== -1 ? data : defaults[field]
      case "PositionInSelect":
        return typeof data === "number" && data >= 0 ? data : defaults[field]
      case "SelectName":
      case "SelectPlaceholder":
      case "SelectHelper":
        return typeof data === "string" ? data : defaults[field]
      case "TemplateSettings":
        return ServiceTemplateSettings.RestoreSettings(data)
    }

    return defaults[field];
  }
}